import React, { useContext, useEffect, useState } from "react";
import {
  Row,
  Column,
  FormGroup,
  TextArea,
  TextInput,
  Form,
  ButtonSet,
  Button,
  DatePicker,
  DatePickerInput,
} from "carbon-components-react";
import { useForm } from "react-hook-form";
import http from "../../http-common";
import { vacancySchema } from "../../services/SchemaService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import CancelButton from "../../components/Buttons/CancelButton";
import SubmitButton from "../../components/Buttons/SubmitButton";
import { AuthContext } from "../../contexts/AuthContext";
import ProvinceCombo from "../../components/Combos/ProvinceCombo";
import DistrictCombo from "../../components/Combos/DistrictCombo";

const VacancyForm = ({ vacancy, operation, disabled }) => {
  const navigate = useNavigate();
  const { user, isCompetitor, isTrainee, isWinner, isIntershipJury } =
    useContext(AuthContext);
  const [company, setCompany] = useState(null);
  const [districts, setDistricts] = useState([]);
  const {
    register,
    reset,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(vacancySchema),
    mode: "onBlur",
  });

  const loadCompany = async () => {
    try {
      const response = await http.get(
        `/internships/companies/nur/NUR${user.username}`
      );
      setCompany(response.data);
    } catch (error) {
      // navigate("../inicio/");
      if (isWinner) {
        toast.error(
          "Não é possível adicionar vagas antes de registar o perfil da empresa. Clique em início para o fazer."
        );
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      data.companyId = company.id;
      const endPoint = "/internships/vacancies";
      const response =
        (await operation) === "CREATE"
          ? http.post(endPoint, data)
          : http.put(endPoint, data);
      navigate("../vagas-para-estagio/");
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  const applyForVacancy = async () => {
    try {
      const response = await http.post(
        `/internships/vacancies/apply/${vacancy?.uuid}/${user.username}`
      );
      navigate("../painel-estagiario/");
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  const getDistricts = async (provinceId) => {
    if (provinceId) {
      try {
        const response = await http.get(
          "/utils/provinces/" + provinceId + "/districts"
        );
        setDistricts(response.data);
      } catch (error) {
        toast.error(error?.response?.data.message);
      }
    } else {
      setDistricts([]);
    }
  };

  useEffect(() => {
    loadCompany();
    reset(vacancy);
    if (vacancy) {
      getDistricts(vacancy.provinceId);
    }
  }, [vacancy]);

  const setStartDate = (e) => {
    setValue("recruitmentStartDate", e.target.value);
  };
  const setEndDate = (e) => {
    setValue("recruitmentEndDate", e.target.value);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {/* <FormGroup legendText="" className="section-header"> */}
      <fieldset className="bx--fieldset">
        <Row className="mt-1">
          <Column sm={12} md={12} lg={12} className="required">
            <TextInput
              id="title"
              labelText="A4. Título da Vaga"
              disabled={!isWinner}
              {...register("title")}
            />
            <p className="field-error">{errors.title?.message}</p>
          </Column>
        </Row>
        <Row>
          <Column xsm={12} sm={12} md={12} lg={12} className="required">
            <TextArea
              id="termsOfReference"
              labelText="A5. Termos de Referência para a Vaga"
              helperText="[Descreva de forma detalhada  o contexto da vaga, as principais atribuições/tarefas a realizar, escopo de trabalho, etc.]"
              rows={5}
              enableCounter
              disabled={!isWinner}
              {...register("termsOfReference")}
            />
            <p className="field-error">{errors.termsOfReference?.message}</p>
          </Column>
        </Row>
        <Row>
          <Column xsm={12} sm={12} md={12} lg={12} className="required">
            <TextArea
              id="requirements"
              labelText="A6. Requisitos/Perfil do Candidato"
              helperText="[Descreva de forma detalhada os requisitos em termos de Educação/formação, Conhecimentos, habilidades técnicas, habilidades sociais que o candidato a esta vaga deve possuir.]"
              rows={5}
              enableCounter
              disabled={!isWinner}
              {...register("requirements")}
            />
            <p className="field-error">{errors.requirements?.message}</p>
          </Column>
        </Row>
      </fieldset>
      <fieldset className="bx--fieldset">
        <legend className="bx--label">A7. Local de trabalho</legend>
        <Row>
          <Column xml={12} sm={6} md={4} lg={4} className="required">
            <ProvinceCombo
              id="provinceId"
              name="provinceId"
              label="Província"
              disabled={!isWinner}
              control={control}
              setDistricts={setDistricts}
            />
            <p className="field-error">{errors.provinceId?.message}</p>
          </Column>

          <Column xml={12} sm={6} md={4} lg={4} className="required">
            <DistrictCombo
              id="districtId"
              name="districtId"
              label="Cidade/Distrito"
              disabled={!isWinner}
              items={districts}
              control={control}
            />
            <p className="field-error">{errors.districtId?.message}</p>
          </Column>
          <Column sm={12} md={4} lg={4} className="required">
            <TextInput
              id="duration"
              labelText="Duração (meses)"
              disabled={!isWinner}
              {...register("duration")}
            />
            <p className="field-error">{errors.duration?.message}</p>
          </Column>
        </Row>
      </fieldset>
      <fieldset className="bx--fieldset">
        <legend className="bx--label">A8. Período de candidatura</legend>
        <Row>
          <Column xml={12} sm={6} md={4} lg={4} className="required">
            <DatePicker locale="en" dateFormat="d/m/Y" datePickerType="single">
              <DatePickerInput
                labelText="Data Início"
                id="recruitmentStartDate"
                name="recruitmentStartDate"
                disabled={!isWinner}
                {...register("recruitmentStartDate")}
                value={vacancy?.recruitmentStartDate}
                onSelect={setStartDate}
              />
            </DatePicker>
            <p className="field-error">
              {errors.recruitmentStartDate?.message}
            </p>
          </Column>

          <Column xml={12} sm={6} md={4} lg={4} className="required">
            <DatePicker locale="en" dateFormat="d/m/Y" datePickerType="single">
              <DatePickerInput
                labelText="Data Fim"
                id="recruitmentEndDate"
                name="recruitmentEndDate"
                disabled={!isWinner}
                {...register("recruitmentEndDate")}
                value={vacancy?.recruitmentEndDate}
                onSelect={setEndDate}
              />
            </DatePicker>
            <p className="field-error">{errors.recruitmentEndDate?.message}</p>
          </Column>
          <Column sm={12} md={4} lg={4} className="required">
            <TextInput
              id="numberOfVacancies"
              labelText="A9. Número de vagas"
              disabled={!isWinner}
              {...register("numberOfVacancies")}
            />
            <p className="field-error">{errors.numberOfVacancies?.message}</p>
          </Column>
        </Row>
        <Row>
          <Column sm={12} md={12} lg={12} className="form-buttons mt-1">
            <ButtonSet>
              {(isTrainee || (isCompetitor && !isWinner)) && (
                <Button type="button" onClick={applyForVacancy}>
                  Candidatar
                </Button>
              )}

              {company && isWinner && <SubmitButton />}
              <CancelButton />
            </ButtonSet>
          </Column>
        </Row>
      </fieldset>
      {/* </FormGroup> */}
    </Form>
  );
};

VacancyForm.defaultProps = {
  disabled: false,
};

export default VacancyForm;
